import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { BaseTimeslotBodyInterface } from '@sport-activities/sdk';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { Type } from '~/component/form/input/enums/type.enum';
import { ModalEditTimeslotsComponent } from '~/component/modal-edit-timeslots/modal-edit-timeslots.component';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalRoleEnum } from '~/component/modal/enums/role.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { urlValidator } from '~/core/validators/url/url.validator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { TimeslotUpdateService } from '~/organizer/timeslot-edition/services/timeslot-update.service';

@Component({
  selector: 'app-organizer-timeslot-edition-live-url',
  templateUrl: './live-url.component.html',
})
export class OrganizerTimeslotEditionLiveUrlComponent
  implements OnDestroy, OnInit
{
  @ViewChild('modalEditTimeslots')
  public modalEditTimeslots: ModalEditTimeslotsComponent;

  private readonly subscriptions = new Subscription();

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly inputType = Type.Number;
  public readonly minimumGroupSize = 1;
  public readonly modalRole = ModalRoleEnum;
  public readonly modalVariant = ModalVariantEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;

  public activity: Activity;
  public formGroup: FormGroup;
  public isLastTimeslot: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public previousUrl: string;
  public timeslot: Timeslot;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly router: Router,
    private readonly organizerSharedActivityService: OrganizerSharedActivityService,
    private readonly organizerSharedLocatableService: OrganizerSharedLocatableService,
    private readonly timeslotUpdateService: TimeslotUpdateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    const { timeslotData } = this.activatedRoute.snapshot.data;

    this.activity = timeslotData.activity;
    this.timeslot = timeslotData.timeslot;
    this.previousUrl = `/organizer.timeslot.manage/${this.timeslot.identifier}`;

    this.formGroup = new FormGroup({
      liveUrl: new FormControl(this.timeslot.liveUrl || '', [
        Validators.required,
        urlValidator(),
      ]),
      noteForAttendees: new FormControl(this.timeslot.noteForAttendees || ''),
    });
  }

  private get partialBody(): Partial<BaseTimeslotBodyInterface> {
    return {
      liveUrl: this.liveUrl.value,
      noteForAttendees: [
        this.organizerSharedLocatableService.buildCurrentLocatableBody(
          this.noteForAttendees.value || null
        ),
      ],
    };
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.timeslot?.liveUrl);
  }

  public get isFormValid(): boolean {
    return this.formGroup.valid && this.formGroup.dirty;
  }

  public get liveUrl(): AbstractControl {
    return this.formGroup.get('liveUrl');
  }

  public get noteForAttendees(): AbstractControl {
    return this.formGroup.get('noteForAttendees');
  }

  private isTimeslotUpdated(timeslot: Timeslot): boolean {
    return Object.keys(this.formGroup.value).every((key) => {
      return (this.formGroup.value[key] || null) === timeslot[key];
    });
  }

  public applyToSession(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    this.subscriptions.add(
      this.organizerSharedActivityService
        .patchTimeslot(this.timeslot.identifier, this.partialBody)
        .pipe(
          mergeMap(() => {
            return this.organizerSharedActivityService
              .getTimeslot(this.timeslot.identifier)
              .pipe(
                repeatUntil((timeslot) => {
                  return this.isTimeslotUpdated(timeslot);
                })
              );
          })
        )
        .subscribe(() => {
          this.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }

  public updateAllTimeslots(): void {
    this.modalEditTimeslots.closeModal();
    this.isOverlayDisplayed = true;

    combineLatest([
      this.organizerSharedActivityService
        .getActivityTimeslots(this.activity?.sku?.toString(), {
          startDate: this.timeslot?.startDate?.toString(),
        })
        .pipe(
          mergeMap((timeslots) => {
            return this.finishPatchTimeslot(timeslots, this.partialBody);
          })
        ),
      this.organizerSharedActivityService
        .getTimeslot(this.timeslot.identifier)
        .pipe(
          repeatUntil((timeslot) => {
            return this.isTimeslotUpdated(timeslot);
          })
        ),
    ]).subscribe(() => {
      this.overlayStatus = SuccessStatusEnum.SUCCESS;
    });
  }

  public finishPatchTimeslot(timeslots, partialBody): Observable<string[]> {
    return this.organizerSharedActivityService.patchTimeslotBulk(
      this.activity?.sku?.toString(),
      timeslots.map((timeslot) => ({
        ...partialBody,
        identifier: timeslot.identifier,
      }))
    );
  }

  public updateSelectedTimeslots(): void {
    this.timeslotUpdateService.setPartialTimeslotBody(this.partialBody);
    this.timeslotUpdateService.setPartialTimeslotUpdates(this.formGroup.value);

    const translatedPath = this.localizeRouter.translateRoute([
      '/organizer.timeslot.manage',
      this.timeslot.identifier,
      'organizer.timeslot-edition.live-url',
      'organizer.timeslot-edition.select-timeslots',
    ]) as string[];

    this.router.navigate(translatedPath);
  }

  public validateForm(): void {
    this.modalEditTimeslots.openModal();
  }
}
